import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const SERVICES = [
  { key: 'avgPest365', name: 'Pest365', color: '#2563eb' },
  { key: 'avgPest365Plus', name: 'Pest365+', color: '#8b5cf6' },
  { key: 'avgOneTime', name: 'One-Time', color: '#f59e0b' },
  { key: 'avgISV', name: 'Init. Visit', color: '#16a34a' },
  { key: 'avgMTC', name: 'Mosquito/Tick', color: '#dc2626' },
];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '16px',
  },
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        padding: '10px 14px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        fontSize: '13px',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '6px', color: '#1f2937' }}>
        {label}
      </div>
      {payload.map((entry) => (
        <div
          key={entry.dataKey}
          style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '2px' }}
        >
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span style={{ fontWeight: 600 }}>
            {entry.value != null ? `$${entry.value.toFixed(2)}` : 'N/A'}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function StateComparisonChart({ data }) {
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => (b.avgPest365 || 0) - (a.avgPest365 || 0));
  }, [data]);

  if (sortedData.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Average Price by State</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No state data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Average Price by State</div>
      <ResponsiveContainer width="100%" height={380}>
        <BarChart data={sortedData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="state"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            tickFormatter={(v) => `$${v}`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            iconType="square"
            iconSize={10}
            wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
          />
          {SERVICES.map(({ key, name, color }) => (
            <Bar
              key={key}
              dataKey={key}
              name={name}
              fill={color}
              radius={[2, 2, 0, 0]}
              maxBarSize={24}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
